import { useMany, useTranslate } from '@refinedev/core';
import { DateField } from '@refinedev/antd';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { RoyaltyReportDetailOut, UserOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { RoyaltyReportStatusTag } from '@/components/settlement/tags';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { formatDateTime, formatUnits } from '@/utils/format';
import { formatLedger } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { PayableAmount } from '@/components/settlement/PayableAmount';
import { reportUnitsCount } from '../statements';
import { RoyaltyReportStatementsCard } from './RoyaltyReportStatementsCard';

const T = 'settlement.royalty_reports';

export const RoyaltyReportGeneralTab = ({
  record,
  onOpenBreakdown,
}: {
  record: RoyaltyReportDetailOut;
  onOpenBreakdown: (statementId: number) => void;
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const verticalDescProps = useVerticalDescProps();

  // Both sides of the approval trail are users; one batch covers them.
  const userIds = [
    ...new Set(
      [record.moderated_by, record.responsed_by].filter(
        (id): id is number => id != null
      )
    ),
  ];
  const { result: usersResult } = useMany<UserOut>({
    resource: 'iam.users',
    ids: userIds,
    queryOptions: { enabled: userIds.length > 0 },
  });
  const userMap = new Map(
    (usersResult?.data ?? []).map((u: UserOut) => [u.id, u])
  );

  const userCell = (userId?: number | null) =>
    orDash(
      userId != null && (
        <ShowIconLink resource="iam.users" id={userId}>
          {userMap.get(userId)?.email ?? userId}
        </ShowIconLink>
      )
    );

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: record.id },
    {
      key: 'period',
      label: t(`${T}.fields.period`),
      children: <DateField value={record.period} format="YYYY-MM" />,
    },
    {
      key: 'publisher_id',
      label: t(`${T}.fields.publisher_id`),
      children: <PublisherIconLink publisherId={record.publisher_id} />,
    },
    {
      key: 'status',
      label: t(`${T}.fields.status`),
      children: <RoyaltyReportStatusTag status={record.status} />,
    },
    {
      key: 'amount',
      label: (
        <HintLabel
          label={t(`${T}.fields.amount_ledger`)}
          hint={t('settlement.hints.amount_ledger')}
        />
      ),
      children: formatLedger(record.amount, record.currency),
    },
    {
      key: 'payable_amount',
      label: (
        <HintLabel
          label={t(`${T}.fields.payable_amount`)}
          hint={t('settlement.hints.payable_amount')}
        />
      ),
      children: (
        <PayableAmount
          amount={record.payable_amount}
          roundingDelta={record.rounding_delta}
          currency={record.currency}
        />
      ),
    },
    {
      key: 'currency',
      label: t(`${T}.fields.currency`),
      children: orDash(record.currency),
    },
    {
      key: 'units_count',
      label: t(`${T}.fields.units_count`),
      children: formatUnits(reportUnitsCount(record)),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const approvalItems: DescriptionsProps['items'] = [
    {
      key: 'moderated_at',
      label: t(`${T}.fields.moderated_at`),
      children: orDash(formatDateTime(record.moderated_at)),
    },
    {
      key: 'moderated_by',
      label: t(`${T}.fields.moderated_by`),
      children: userCell(record.moderated_by),
    },
    {
      key: 'approved_at',
      label: t(`${T}.fields.approved_at`),
      children: orDash(formatDateTime(record.approved_at)),
    },
    {
      key: 'rejected_at',
      label: t(`${T}.fields.rejected_at`),
      children: orDash(formatDateTime(record.rejected_at)),
    },
    {
      key: 'responsed_by',
      label: t(`${T}.fields.responsed_by`),
      children: userCell(record.responsed_by),
    },
    {
      key: 'rejection_reason',
      label: t(`${T}.fields.rejection_reason`),
      span: 2,
      children: orDash(
        record.rejection_reason && (
          <Typography.Text>{record.rejection_reason}</Typography.Text>
        )
      ),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          {/* Empty until the platform moves — a draft has no trail yet. */}
          <Card title={t(`${T}.sections.approval`)}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={approvalItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 420px' : '0 1 360px'}>
        <RoyaltyReportStatementsCard
          record={record}
          onOpenBreakdown={onOpenBreakdown}
        />
      </Col>
    </Row>
  );
};
